import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Generic file exports (GEN-2683).
 *
 * Any "export this screen" procedure starts a background job and answers with
 * its id straight away. The file is built by a worker and streamed into blob
 * storage; the browser polls `core.backgroundJobs.get` and, once the job has
 * succeeded, asks this procedure for a short-lived signed link. The file never
 * passes through a web response, so its size costs the API nothing.
 */

/** What every export-starting procedure returns. */
export const StartFileExportResponseSchema = z.object({
  backgroundJobId: z.uuid(),
});
export type StartFileExportResponse = z.infer<
  typeof StartFileExportResponseSchema
>;

export const GetFileExportDownloadInputSchema = z.object({
  backgroundJobId: z.uuid(),
});

export const GetFileExportDownloadResponseSchema = z.object({
  /** Signed, short-lived (minutes), and downloads under `filename`. */
  url: z.string(),
  filename: z.string(),
  rowCount: z.number(),
  /** The export hit its row cap; the file says so on its info sheet. */
  truncated: z.boolean(),
  sizeBytes: z.number(),
});
export type GetFileExportDownloadResponse = z.infer<
  typeof GetFileExportDownloadResponseSchema
>;

export const fileExportsContracts = {
  getDownloadUrl: oc
    .input(GetFileExportDownloadInputSchema)
    .output(GetFileExportDownloadResponseSchema)
    .route({
      method: "GET",
      summary:
        "A short-lived download link for one of the signed-in user's finished file exports",
      description:
        "Returns a temporary download link, file name, row count, size and whether rows were truncated for a finished export job.",
    }),
} as const;
